import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { ReservationError } from '../../models/errors/reservation-error.class';
import { ReserveAnswer, ReserveRequest } from '../../lead.model';
import { BaseHttpService } from '../base-http.service';

@Injectable({
  providedIn: 'root',
})
export class LeadReservationsService extends BaseHttpService {
  constructor(private readonly http: HttpClient) {
    super();
  }

  /**
   * Requests a reservation for the given lead IDs and expects typical
   * responses to be either 200 or 204 for success, or 409 for conflict.
   * @param leadIds
   */
  requestReservationForLeads(leadIds: string[]): Observable<string[]> {
    const requestBody: ReserveRequest = {
      ids: leadIds,
    };

    return this.applyHttpOperators(
      this.http.post<ReserveAnswer>(
        environment.backend_host + '/reserve',
        requestBody,
        { observe: 'response' },
      ),
    ).pipe(
      map((response) => {
        if (response.body?.failedToReserve) {
          // If the response contains failedToReserve, return the list of IDs
          return response.body.failedToReserve;
        }
        return [];
      }),
    );
  }

  removeReservationForLeads(leadIds: string[]): Observable<void> {
    const requestBody: { ids: string[] } = {
      ids: leadIds,
    };

    return this.applyHttpOperators(
      this.http.post(environment.backend_host + '/unreserve', requestBody).pipe(
        map(() => {
          return;
        }),
      ),
    );
  }

  /**
   * Custom error handler for reservation requests.
   * Overrides the base class method.
   */
  protected override handleError(
    error: TimeoutError | HttpErrorResponse,
  ): Observable<never> {
    if (
      error instanceof HttpErrorResponse &&
      error.status === 409 &&
      !!error.error &&
      Array.isArray(error.error.failedToReserve)
    ) {
      // Conflict: return a list of ids
      return throwError(
        (): ReservationError =>
          new ReservationError(
            'The following leads could not be reserved due to a conflict',
            true,
            error.error.failedToReserve,
          ),
      );
    }

    return super.handleError(error);
  }
}
